import React, {
  type Dispatch,
  type SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAtom, useAtomValue, useSetAtom } from "jotai";
import {
  type Dependency,
  type DeviceProviderSettings,
  type RunExecution,
  type OptimizationState,
  type SettingsData,
  type TestFolder,
  type TestTemplate,
  type UnparsedTest,
} from "../../utils/types";
import { AuthContext } from "../../auth/AuthContext";
import { useDocumentTitle } from "../../hooks/useDocumentTitle";
import { buildDialogStateAtom, SelectBuildDialog } from "./select-build-dialog";
import getBuildsAtom from "../../atoms/get-builds-atom";
import GenericDatatable from "../../components/generic-datatable";
import {
  getTestsAtom,
  buildTestsListQuery,
  type TestListData,
} from "../../atoms/testing-settings-atoms";
import {
  GridActionsCellItem,
  type GridColDef,
  type GridColumnVisibilityModel,
  type GridComparatorFn,
  gridFilteredSortedRowEntriesSelector,
  gridQuickFilterValuesSelector,
  type GridRenderCellParams,
  type GridRowSelectionModel,
  GridToolbarQuickFilter,
  useGridApiContext,
  useGridApiRef,
  type GridInitialStateCommunity,
  type GridFilterModel,
  type GridCallbackDetails,
  Toolbar,
  QuickFilter,
  QuickFilterTrigger,
  QuickFilterControl,
  QuickFilterClear,
  ToolbarButton,
} from "@mui/x-data-grid";
import { DateTime } from "luxon";

import Chip from "@mui/material/Chip";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  arrayUnion,
  collection,
  doc,
  documentId,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  runTransaction,
  updateDoc,
  where,
} from "firebase/firestore";
import { customFirestore } from "../../firebase";
import { auditMeta } from "../../utils/auditMeta";
import { arrayRemove } from "@firebase/firestore";
import {
  getErrorMessage,
  removeItemFromArray,
  replaceItemFomArray,
  trackSentryException,
} from "../../utils/helpers";
import { customAlertStateAtom } from "../../components/custom-alert";
import Button from "@mui/material/Button";
import {
  Add,
  ChevronLeft,
  Delete,
  DeleteOutline,
  Menu,
  SnippetFolderOutlined,
  Search,
  Cancel,
  CancelOutlined,
  Close,
} from "@mui/icons-material";
import { BACKEND_SERVER_URL } from "../../constants/aws-constants";
import {
  IconButton,
  InputAdornment,
  Popover,
  Tooltip,
  Typography,
} from "@mui/material";
import axios, { type AxiosError } from "axios";
import apiClient from "../../utils/api-client";
import TestsTreeView, {
  foldersStateAtom,
  kAllTestsId,
} from "./tests-tree-view";
import styled from "@emotion/styled";
import { styled as muiStyled } from "@mui/material/styles";
import { AsyncConfirmationModal } from "../../components/asyncConfirmationModal";
import MoveToFolderDialog, {
  getSubFolders,
  getFolderAndDescendantIds,
  moveToFolderStateAtom,
} from "./move-to-folder-dialog";
import { companyDataAtom } from "../test-run/test-run-atoms";
import { atomWithStorage } from "jotai/utils";
import CloneTestsDialog from "./clone-tests-dialog";

const TestsHome = () => {
  useDocumentTitle("Tests");
  const { user } = useContext<any>(AuthContext);

  const navigate = useNavigate();

  const navigateTo = useCallback(
    (path: string) => {
      navigate(path);
    },
    [navigate]
  );

  const [searchParams] = useSearchParams();
  const setFoldersState = useSetAtom(foldersStateAtom);
  const [expandFolders, setExpandFolders] = useAtom(expandFoldersAtom);
  // Sync the ?folder=... deep link into state exactly once — on mount if present,
  // or when it first appears (e.g. after a post-login redirect). Opening a folder
  // link also reveals the folder sidebar. Guarded with a ref so internal folder/
  // test selection (which rewrites ?folder) doesn't clobber the selection.
  const folderDeepLinkSyncedRef = useRef(false);
  useEffect(() => {
    if (folderDeepLinkSyncedRef.current) return;
    const folderParam = searchParams.get("folder");
    if (!folderParam) return;
    folderDeepLinkSyncedRef.current = true;
    setFoldersState((prev) => ({
      ...prev,
      selectedFolderId: folderParam,
      selectedTestId: undefined,
    }));
    setExpandFolders(true);
  }, [searchParams, setFoldersState, setExpandFolders]);

  const companyDataQuery = useAtomValue(companyDataAtom(user.companyId));
  const allBuildsQuery = useAtomValue(
    getBuildsAtom({ organizationId: user.companyId, limit: 20 })
  );
  const setBuildDialogState = useSetAtom(buildDialogStateAtom);

  const enableMarkdownEditor =
    companyDataQuery.data?.settings?.enableMarkdownEditor === true;

  const handleNewTest = useCallback(() => {
    setBuildDialogState((prev) => ({
      ...prev,
      open: true,
      actionTitle: "Add new test",
      handleConfirm: async ({
        buildId,
        queryParams,
      }: {
        buildId: string;
        queryParams: string;
      }) => {
        const path = enableMarkdownEditor
          ? `/gpt-driver/build/${buildId}/markdown${queryParams}`
          : `/gpt-driver/build/${buildId}${queryParams}`;
        navigateTo(path);
      },
      type: "redirect",
    }));
  }, [navigateTo, setBuildDialogState, enableMarkdownEditor]);

  const enableFolders =
    companyDataQuery.data?.settings?.enableFoldersFeature ?? false;
  return (
    <ContentWrapper enableFolders={enableFolders} expandFolders={expandFolders}>
      <SelectBuildDialog />
      <TitleWrapper>
        <PageTitle>Tests</PageTitle>
        <AddTestButton
          title={
            allBuildsQuery?.data?.length === 0
              ? "Please upload a build file first"
              : ""
          }
        >
          <span>
            <Button
              variant={"outlined"}
              onClick={handleNewTest}
              startIcon={<Add />}
              disabled={
                allBuildsQuery?.data?.length === 0 ||
                allBuildsQuery.isLoading ||
                allBuildsQuery.isError
              }
            >
              Add new test
            </Button>
          </span>
        </AddTestButton>
      </TitleWrapper>
      {enableFolders && (
        <FoldersWrapper expandFolders={expandFolders}>
          <ExpandFolderIcon
            onClick={() => {
              setExpandFolders((prev) => !prev);
            }}
            expandFolders={expandFolders}
          >
            {expandFolders ? <ChevronLeft /> : <Menu />}
          </ExpandFolderIcon>
          <TestsTreeView expandFolders={expandFolders} />
        </FoldersWrapper>
      )}

      <TableWrapper>
        {!companyDataQuery.isLoading && !companyDataQuery.isError && (
          <DatatableWrapper settingsData={companyDataQuery.data.settings} />
        )}
      </TableWrapper>
    </ContentWrapper>
  );
};

const ExpandFolderIcon = styled(IconButton)(
  ({ expandFolders }: { expandFolders: boolean }) => ({
    justifySelf: expandFolders ? "end" : "start",
  })
);

const ContentWrapper = styled.div(
  ({
    enableFolders,
    expandFolders,
  }: {
    enableFolders: boolean;
    expandFolders: boolean;
  }) => ({
    gridArea: "content",
    display: "grid",
    transition: "200ms",
    transitionDelay: !expandFolders ? "100ms" : "0ms",
    ...(enableFolders
      ? {
          gridTemplateAreas: `
    "title-wrapper title-wrapper"
    "folders datatable"
  `,
        }
      : {
          gridTemplateAreas: `
    "title-wrapper title-wrapper"
    "datatable datatable"
  `,
        }),
    columnGap: "12px",
    gridTemplateColumns: expandFolders ? "288px 1fr" : "40px 1fr",
    gridTemplateRows: "80px calc(100vh - 205px)",
    backgroundColor: "#e8e8e8",
    padding: "16px",
    borderRadius: "8px",
    margin: "16px",
  })
);

const TitleWrapper = styled.div`
  grid-area: title-wrapper;
  display: flex;
  justify-content: space-between;
`;

const PageTitle = styled.p`
  font-size: 24px;
  font-weight: 500;
`;

const FoldersWrapper = styled.div(
  ({ expandFolders }: { expandFolders: boolean }) => ({
    gridArea: "folders",
    display: "grid",
    gridTemplateAreas: expandFolders
      ? `"expand-icon"
    "folders"`
      : `"expand-icon"`,
    gridTemplateRows: expandFolders ? "40px minmax(0, 1fr)" : "40px",
    rowGap: "4px",
  })
);

const TableWrapper = styled.div`
  grid-area: datatable;
  overflow: auto;
`;

const AddTestButton = muiStyled(Tooltip)(() => ({
  gridArea: "actions",
  display: "flex",
  justifyContent: "flex-end",
  alignItems: "flex-start",
}));

const DatatableWrapper = ({ settingsData }: { settingsData: SettingsData }) => {
  const apiRef = useGridApiRef();
  const queryClient = useQueryClient();

  const [searchParams, setSearchParams] = useSearchParams();
  const initialSearchQRef = useRef(searchParams.get("q") ?? "");
  const initialSearchQ = initialSearchQRef.current;

  const navigate = useNavigate();

  const navigateTo = useCallback(
    (path: string, match?: string) => {
      if (match != null) {
        window.open(`${path}?match=${encodeURIComponent(match)}`, "_blank");
      } else {
        navigate(path);
      }
    },
    [navigate]
  );

  const { user } = useContext<any>(AuthContext);
  const foldersState = useAtomValue(foldersStateAtom);

  const testListQuery = useAtomValue(
    getTestsAtom({
      organizationId: user.companyId,
      folderId: foldersState.selectedFolderId,
    })
  );

  useEffect(() => {
    if (testListQuery.data != null) {
      apiRef.current.setRowSelectionModel({
        type: "include",
        ids:
          foldersState.selectedTestId != null
            ? new Set([foldersState.selectedTestId])
            : new Set(),
      });
    }
  }, [
    foldersState.selectedTestId,
    foldersState.selectedFolderId,
    testListQuery.data,
    apiRef,
  ]);

  // The tests grid is a one-time fetch (getTestsAtom), so optimizationState —
  // the "Optimizing 2/5" progress the FAPI loop writes as it runs, and the
  // flip to "Optimization cancelled"/"Optimized" — goes stale until a page
  // refresh. Subscribe to the current folder's tests and write changed
  // optimizationState back into the React Query cache that backs the grid.
  // (We update the cache, not apiRef.updateRows: the grid's `rows` are
  // controlled by this query's data, and updateRows is unreliable / reset for
  // controlled rows.)
  useEffect(() => {
    const organizationId = user.companyId;
    if (organizationId == null) return;
    const folderId = foldersState.selectedFolderId;
    const queryKey = ["getTests", organizationId, folderId];
    const unsubscribe = onSnapshot(
      buildTestsListQuery(organizationId, folderId),
      (snapshot) => {
        const changedById = new Map<string, OptimizationState | undefined>();
        snapshot.docChanges().forEach((change) => {
          // Process "added" too, not just "modified": getTestsAtom doesn't
          // refetch on mount, so when this listener re-subscribes (e.g. user
          // navigates Reports -> Tests after cancelling) the first snapshot
          // re-delivers every doc as "added" — that's how we reconcile a state
          // change that happened while the Tests page was unmounted. The
          // equality guard below keeps that from re-rendering unchanged rows.
          if (change.type === "removed") return;
          changedById.set(
            change.doc.id,
            change.doc.data()?.optimizationState as OptimizationState | undefined
          );
        });
        if (changedById.size === 0) return;
        queryClient.setQueryData<TestListData>(queryKey, (old) => {
          if (old == null) return old;
          let touched = false;
          const values = old.values.map((test) => {
            if (!changedById.has(test.id)) return test;
            const next = changedById.get(test.id);
            if (
              JSON.stringify(test.optimizationState ?? null) ===
              JSON.stringify(next ?? null)
            ) {
              return test; // unchanged — avoid a needless re-render
            }
            touched = true;
            return { ...test, optimizationState: next };
          });
          return touched ? { ...old, values } : old;
        });
      },
      (error) => {
        trackSentryException(error);
      }
    );
    return () => {
      unsubscribe();
    };
  }, [user.companyId, foldersState.selectedFolderId, queryClient]);

  const [rowSelectionModel, setRowSelectionModel] =
    useState<GridRowSelectionModel>({
      type: "include",
      ids: new Set(),
    });

  const [deleteTestDialogState, setDeleteTestDialogState] =
    useState<TestTemplate | null>(null);
  const setBuildDialogState = useSetAtom(buildDialogStateAtom);

  const handleDeleteTest = useCallback((testTemplate: TestTemplate) => {
    setDeleteTestDialogState(testTemplate);
  }, []);

  const resetDeleteTestDialogState = useCallback(() => {
    setDeleteTestDialogState(null);
  }, []);

  const enableMarkdownEditor =
    settingsData?.enableMarkdownEditor === true;

  const handleEditTest = useCallback(
    (testId: string, match?: string) => {
      setBuildDialogState((prev) => ({
        ...prev,
        open: true,
        actionTitle: "Edit test",
        handleConfirm: async ({
          buildId,
          queryParams,
        }: {
          buildId: string;
          queryParams?: string;
        }) => {
          const path = enableMarkdownEditor
            ? `/gpt-driver/build/${buildId}/test/${testId}/markdown${queryParams ?? ""}`
            : `/gpt-driver/build/${buildId}/test/${testId}${queryParams ?? ""}`;
          navigateTo(path, match);
        },
        type: "redirect",
      }));
    },
    [navigateTo, setBuildDialogState, enableMarkdownEditor]
  );

  const setCustomAlertState = useSetAtom(customAlertStateAtom);

  const deleteTestMutation = useMutation({
    mutationFn: async () => {
      const testsRef = collection(customFirestore, "custom-tests");
      const testId = deleteTestDialogState!.id;
      const q = query(
        testsRef,
        where("organisationId", "==", user.companyId),
        where(documentId(), "!=", testId),
        orderBy(documentId(), "asc")
      );
      const querySnapshot = await getDocs(q);
      const testWithDependencies: Array<
        UnparsedTest & {
          dependencies: Dependency[];
        }
      > = await Promise.all(
        querySnapshot.docs.map(async (test) => {
          const unparsedTest = test.data() as UnparsedTest;
          const customParsedTest: UnparsedTest & {
            dependencies: Dependency[];
          } = { ...unparsedTest, dependencies: [] };

          customParsedTest.id = test.id;

          // Access sub-collection and add to test data
          const subCollectionRef = collection(test.ref, "dependencies");
          const subCollectionSnapshot = await getDocs(subCollectionRef);

          customParsedTest.dependencies = subCollectionSnapshot.docs.map(
            (doc) => doc.data() as Dependency
          );

          return customParsedTest;
        })
      );

      const dependentTests = testWithDependencies.reduce<UnparsedTest[]>(
        (prev, currentTest) => {
          const directDependencies =
            currentTest.dependencies?.filter(
              (dependency) => dependency.testId === testId
            ) ?? [];
          if (directDependencies.length > 0) {
            return [...prev, currentTest];
          }
          return prev;
        },
        []
      );

      if (dependentTests.length > 0) {
        setCustomAlertState({
          open: true,
          type: "error",
          title: "Test couldn't be deleted, the following tests depend on it:",
          listContent: dependentTests
            .slice(0, 6)
            .map((dependentTest) => dependentTest.title),
        });
      } else {
        // Pre-delete actor stamp: a Firestore delete carries no payload, so
        // the audit trigger reads `updatedBy` from the doc's last state. Stamp
        // the deleter now so test.deleted is attributed to them, not to the
        // last editor. (Stamp-only update → no test.updated event.)
        await updateDoc(
          doc(customFirestore, "custom-tests", testId),
          auditMeta()
        );
        const testBelongedToFolderId = await runTransaction(
          customFirestore,
          async (transaction) => {
            const testRef = doc(customFirestore, "custom-tests", testId);
            const testSnap = await transaction.get(testRef);
            const testData = testSnap.data() as UnparsedTest;
            if (testData.folderId != null) {
              const folderRef = doc(
                customFirestore,
                "folders",
                testData.folderId
              );
              const folderSnap = await transaction.get(folderRef);
              const folder = folderSnap.data() as TestFolder;
              const testToRemove = folder.tests?.find(
                (test) => test.id === testId
              );
              if (testToRemove != null) {
                transaction.update(folderRef, {
                  tests: arrayRemove(testToRemove),
                });
              }
            }
            transaction.delete(testRef);
            return testData.folderId;
          }
        );
        queryClient.removeQueries({
          queryKey: ["getTests"],
        });
        await queryClient.invalidateQueries({
          queryKey: ["getFolders"],
        });
        getTestsAtom.remove({
          organizationId: user.companyId,
          folderId: kAllTestsId,
        });
        if (testBelongedToFolderId) {
          getTestsAtom.remove({
            organizationId: user.companyId,
            folderId: testBelongedToFolderId,
          });
        }

        setCustomAlertState({
          open: true,
          type: "success",
          title: "Test has been deleted successfully",
        });
      }
    },
    onError: (error) => {
      console.error("Error deleting", error);
      trackSentryException(error);
      setCustomAlertState({
        open: true,
        type: "error",
        title: "Something went wrong",
        description: `${error}`,
      });
    },
    onSettled: () => {
      resetDeleteTestDialogState();
    },
  });

  const [columnVisibilityModel, setColumnVisibilityModel] =
    useState<GridColumnVisibilityModel>({
      createdAt: false,
      parsedCommands: false,
      runHistory: settingsData.runHistoryEnabled ?? false,
    });

  const onFilterModelChange: (
    model: GridFilterModel,
    details: GridCallbackDetails<"filter">
  ) => void = (model, details) => {
    const filteredItems = gridFilteredSortedRowEntriesSelector(apiRef);
    const searchText = gridQuickFilterValuesSelector(apiRef)!
      .filter((word) => word !== "")
      .join(" ");

    const newCommandsVisibility =
      searchText.length > 0 &&
      filteredItems.some((filteredItem) =>
        (filteredItem.model.parsedCommands ?? filteredItem.model.commands)
          .toLowerCase()
          .includes(searchText.toLowerCase())
      );

    if (filteredItems.length > 0) {
      setColumnVisibilityModel?.((prev) => ({
        ...prev,
        parsedCommands: newCommandsVisibility,
      }));
    }

    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (searchText) {
          next.set("q", searchText);
        } else {
          next.delete("q");
        }
        return next;
      },
      { replace: true }
    );
  };

  const tableConfig = useMemo(
    () => ({
      columns: [
        {
          field: "title",
          headerName: "Title",
          sortable: true,
          flex: 1,
          renderCell: (params: GridRenderCellParams<TestTemplate>) => {
            const row = params.row;

            const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(
              null
            );

            const handlePopoverOpen = (
              event: React.MouseEvent<HTMLElement>
            ) => {
              setAnchorEl(event.currentTarget);
            };

            const handlePopoverClose = () => {
              setAnchorEl(null);
            };

            const open = Boolean(anchorEl);

            const foldersState = useAtomValue(foldersStateAtom);

            const folderPath = useMemo(() => {
              if (
                row.folderId != null &&
                foldersState.selectedFolderId === kAllTestsId
              ) {
                const allFolders = getSubFolders(foldersState.folders!);
                return allFolders.find((folder) => folder.id === row.folderId)
                  ?.path;
              }
              return undefined;
            }, [row.folderId, foldersState.folders]);

            return (
              <div
                className={
                  "flex gap-2 w-full h-full hover:cursor-pointer items-center"
                }
                onClick={() => {
                  handleEditTest(row.id);
                }}
              >
                <span className={"flex-1 overflow-ellipsis"}>
                  {params.value}
                </span>
                <OptimizationBadge state={row.optimizationState} />
                {row.folderId != null &&
                  foldersState.selectedFolderId === kAllTestsId && (
                    <div
                      style={{
                        width: "3ch",
                        flexShrink: 0,
                      }}
                    >
                      <SnippetFolderOutlined
                        onMouseEnter={handlePopoverOpen}
                        onMouseLeave={handlePopoverClose}
                      />
                      <Popover
                        id="mouse-over-popover"
                        sx={{
                          pointerEvents: "none",
                        }}
                        open={open}
                        anchorEl={anchorEl}
                        anchorOrigin={{
                          vertical: "bottom",
                          horizontal: "left",
                        }}
                        transformOrigin={{
                          vertical: "top",
                          horizontal: "left",
                        }}
                        onClose={handlePopoverClose}
                        disableRestoreFocus
                      >
                        <Typography variant={"body2"} sx={{ p: 1 }}>
                          {folderPath}
                        </Typography>
                      </Popover>
                    </div>
                  )}
              </div>
            );
          },
        },
        {
          field: "parsedCommands",
          headerName: "Commands",
          width: 240,
          valueGetter: (value, row) => {
            return row;
          },
          getApplyQuickFilterFn: (value) => {
            return (cellValue) => {
              const searchText = gridQuickFilterValuesSelector(apiRef)!
                .filter((word) => word !== "")
                .join(" ");

              const fullText = cellValue.parsedCommands ?? cellValue.commands;

              return fullText.toLowerCase().includes(searchText.toLowerCase());
            };
          },
          renderCell: (params: GridRenderCellParams<TestTemplate>) => {
            const row = params.row;

            const searchText = gridQuickFilterValuesSelector(apiRef)!
              .filter((word) => word !== "")
              .join(" ");

            const fullText = row.parsedCommands ?? row.commands;

            const indexOfMatch = fullText
              .toLowerCase()
              .indexOf(searchText.toLowerCase());
            if (indexOfMatch == -1) {
              return <div> - </div>;
            } else {
              const match = fullText.substring(
                indexOfMatch,
                indexOfMatch + searchText.length
              );

              return (
                <div className={"w-full overflow-x-auto"}>
                  <Tooltip title={"Edit selected"}>
                    <span
                      onClick={() => {
                        handleEditTest(row.id, match);
                      }}
                      className={"bg-amber-500  hover:cursor-pointer"}
                    >
                      {match}
                    </span>
                  </Tooltip>
                  <span>
                    {fullText.substring(indexOfMatch + searchText.length)}
                  </span>
                </div>
              );
            }
          },
        },
        {
          field: "tags",
          headerName: "Tags",
          sortable: true,
          width: 240,
          sortComparator: tagsComparator,
          renderCell: (params: GridRenderCellParams<TestTemplate>) => {
            return <TagsCell params={params} />;
          },
        },
        {
          field: "runHistory",
          headerName: "Run History",
          sortable: true,
          hideable: true,
          width: 158,
          sortComparator: runHistoryComparator,
          renderCell: (params: GridRenderCellParams<TestTemplate>) => {
            return (
              params.value.length > 0 && (
                <RunHistoryCell runHistory={params.value} />
              )
            );
          },
        },
        {
          field: "createdAt",
          headerName: "Last edited at",
          sortable: true,
          hideable: true,
          width: 128,
          valueGetter: (value) => DateTime.fromJSDate(value.toDate()),
          renderCell: (params: GridRenderCellParams<TestTemplate>) => {
            return (
              <div className={"flex justify-center items-center h-full"}>
                <Tooltip title={params.value.toFormat("yyyy-MM-dd hh:mm a")}>
                  <p className={"text-xs"}>{params.value.toRelative()}</p>
                </Tooltip>
              </div>
            );
          },
        },
        {
          field: "actions",
          type: "actions",
          width: 56,
          getActions: ({ row }: { row: TestTemplate }) => [
            <GridActionsCellItem
              icon={<DeleteOutline />}
              label="Delete"
              onClick={() => {
                handleDeleteTest(row);
              }}
              color="inherit"
            />,
          ],
        },
      ] as GridColDef[],
      initialState: {
        sorting: {
          sortModel: [{ field: "title", sort: "asc" }],
        },
        ...(initialSearchQ
          ? {
              filter: {
                filterModel: {
                  items: [],
                  quickFilterValues: initialSearchQ
                    .split(/\s+/)
                    .filter(Boolean),
                },
              },
            }
          : {}),
      } as GridInitialStateCommunity,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const [displayUncategorizedOnly, setDisplayUncategorizedOnly] =
    useState(false);

  // The "All tests" list (cached — it's the default view) is the source for a
  // specific folder so we can include tests from its sub-folders too. Falls back
  // to the folder's direct tests until the all-tests list has loaded.
  const allTestsQuery = useAtomValue(
    getTestsAtom({ organizationId: user.companyId, folderId: kAllTestsId })
  );

  const filteredData = useMemo(() => {
    const folderId = foldersState.selectedFolderId;
    if (folderId === kAllTestsId) {
      return displayUncategorizedOnly
        ? testListQuery?.data?.values.filter((item) => item.folderId == null)
        : testListQuery?.data?.values;
    }

    // A specific folder: show its tests AND those in all of its sub-folders.
    const folderIds = new Set(
      getFolderAndDescendantIds(foldersState.folders ?? [], folderId ?? "")
    );
    const source =
      allTestsQuery.data?.values ?? testListQuery?.data?.values ?? [];
    return source.filter(
      (item) => item.folderId != null && folderIds.has(item.folderId)
    );
  }, [
    testListQuery?.data?.values,
    allTestsQuery.data?.values,
    foldersState.selectedFolderId,
    foldersState.folders,
    displayUncategorizedOnly,
  ]);

  return (
    <>
      <MoveToFolderDialog />
      <AsyncConfirmationModal
        title={"Are you sure you want to delete the test?\n"}
        actionTitle={"Delete"}
        backTitle={"Close"}
        closeHandler={resetDeleteTestDialogState}
        description={`Test: ${deleteTestDialogState?.title}`}
        open={deleteTestDialogState != null}
        actionHandler={deleteTestMutation.mutateAsync}
        actionIcon={<Delete />}
        submitting={deleteTestMutation.isPending}
      />
      <GenericDatatable
        apiRef={apiRef}
        query={testListQuery}
        onFilterModelChange={onFilterModelChange}
        data={filteredData}
        columnVisibilityModel={columnVisibilityModel}
        setColumnVisibilityModel={setColumnVisibilityModel}
        columns={tableConfig.columns}
        initialState={tableConfig.initialState}
        rowSelectionModel={rowSelectionModel}
        setRowSelectionModel={setRowSelectionModel}
        toolbarActions={ToolbarActions}
        enableFolders={settingsData.enableFoldersFeature}
        noRowsTitle={"No tests defined yet"}
        setDisplayUncategorizedOnly={setDisplayUncategorizedOnly}
        displayUncategorizedOnly={displayUncategorizedOnly}
      />
    </>
  );
};

const StyledQuickFilter = styled(QuickFilter)({
  display: "flex",
  alignItems: "center",
});

const StyledTextField = styled(TextField)({
  width: 260,
});

const ToolbarActions = ({
  rowSelectionModel,
  setRowSelectionModel,
  enableFolders,
  displayUncategorizedOnly,
  setDisplayUncategorizedOnly,
}: any) => {
  return (
    <Toolbar
      sx={{
        paddingInline: "1rem",
        paddingBlock: "1.333rem",
        display: "flex",
        justifyContent: "space-between",
        borderBottomColor: "rgb(238, 238, 238)",
        borderBottomWidth: 1,
        borderBottomStyle: "solid",
        alignItems: "center",
      }}
    >
      <StyledQuickFilter debounceMs={500}>
        <QuickFilterControl
          render={({ ref, ...controlProps }, state) => (
            <StyledTextField
              {...controlProps}
              inputRef={ref}
              aria-label="Search"
              placeholder="Search..."
              variant="standard"
              size="small"
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment
                      position="start"
                      sx={{
                        paddingBlock: "5px",
                      }}
                    >
                      <Search />
                    </InputAdornment>
                  ),
                  endAdornment: state.value ? (
                    <InputAdornment position="end">
                      <QuickFilterClear
                        edge="end"
                        size="small"
                        aria-label="Clear search"
                        // material={{sx: {padding: 0}}}
                        // material={{
                        //   sx: {
                        //     marginRight: -0.75,
                        //     padding: "4px",
                        //   },
                        // }}
                      >
                        <Close />
                      </QuickFilterClear>
                    </InputAdornment>
                  ) : null,
                  ...controlProps.slotProps?.input,
                },
                ...controlProps.slotProps,
              }}
            />
          )}
        />
      </StyledQuickFilter>

      <TestTriggers
        rowSelectionModel={rowSelectionModel}
        setRowSelectionModel={setRowSelectionModel}
        enableFolders={enableFolders}
        displayUncategorizedOnly={displayUncategorizedOnly}
        setDisplayUncategorizedOnly={setDisplayUncategorizedOnly}
      />
    </Toolbar>
  );
};

const TestTriggers = ({
  rowSelectionModel,
  setRowSelectionModel,
  enableFolders,
  setDisplayUncategorizedOnly,
  displayUncategorizedOnly,
}: {
  rowSelectionModel: GridRowSelectionModel;
  setRowSelectionModel: Dispatch<SetStateAction<GridRowSelectionModel>>;
  enableFolders: boolean;
  displayUncategorizedOnly: boolean;
  setDisplayUncategorizedOnly: Dispatch<SetStateAction<boolean>>;
}) => {
  const navigate = useNavigate();

  const navigateTo = useCallback(
    (path: string) => {
      navigate(path);
    },
    [navigate]
  );

  const setBuildDialogState = useSetAtom(buildDialogStateAtom);
  const useAuth = () => useContext<any>(AuthContext);
  const { user } = useAuth();
  const setCustomAlertState = useSetAtom(customAlertStateAtom);
  const [cloneTestsDialogOpen, setCloneTestsDialogOpen] = useState(false);

  const runTests = useCallback(
    (multipleInteractions: boolean) => {
      setBuildDialogState((prev) => ({
        ...prev,
        open: true,
        type: "run",
        actionTitle: `Run test${rowSelectionModel.ids.size > 1 ? "s" : ""}`,
        handleConfirm: async ({
          buildId,
          deviceProviderSettings,
        }: {
          buildId: string;
          deviceProviderSettings?: DeviceProviderSettings;
        }) => {
          try {
            const url = `${BACKEND_SERVER_URL}/createSuite`;
            console.log("calling: ", url);

            const body = {
              organisationId: user.companyId,
              uploadId: buildId,
              customTestIds: [...rowSelectionModel.ids],
              ...(multipleInteractions && {
                iterations: 3,
              }),
              trigger: "web",
              deviceProviderSettings,
            };

            await axios(url, {
              method: "POST",
              data: body,
              timeout: 30000,
            });

            navigateTo(`/gpt-driver/reports`);
          } catch (error) {
            trackSentryException(error);
            let errorDescription = getErrorMessage(
              `Could not start running ${
                rowSelectionModel.ids.size > 1 ? "tests" : "test"
              }`
            );

            if (
              axios.isAxiosError(error) &&
              (error as AxiosError)?.response?.status === 429
            ) {
              errorDescription =
                "You have too many cloud runs in process currently and therefore cannot start another one. Please try again once another cloud run has finished.";
            }

            setCustomAlertState({
              open: true,
              type: "error",
              title: "Something went wrong",
              description: errorDescription,
            });

            console.error("Error:", error);
          }
          setRowSelectionModel({
            type: "include",
            ids: new Set(),
          });
        },
      }));
    },
    [
      navigateTo,
      rowSelectionModel,
      setBuildDialogState,
      setCustomAlertState,
      setRowSelectionModel,
      user.companyId,
    ]
  );

  const apiRef = useGridApiContext();

  const cancelOptimize = useCallback(async () => {
    if (rowSelectionModel.ids.size !== 1) {
      return;
    }
    const [testId] = [...rowSelectionModel.ids] as string[];
    try {
      await apiClient.post("/cancel_test_optimization", {
        organisationId: user.companyId,
        testId,
      });
      setCustomAlertState({
        open: true,
        type: "success",
        title: "Optimization cancelled",
        description:
          "The in-flight run will finish, but no further runs will be" +
          " dispatched for this test.",
      });
    } catch (error) {
      trackSentryException(error);
      setCustomAlertState({
        open: true,
        type: "error",
        title: "Could not cancel optimization",
        description: getErrorMessage("", error),
      });
    }
  }, [rowSelectionModel, setCustomAlertState, user.companyId]);

  const singleSelectedTestId =
    rowSelectionModel.ids.size === 1
      ? ([...rowSelectionModel.ids][0] as string)
      : null;

  // The tests grid is a one-time fetch (getTestsAtom), so the Optimize/Cancel
  // button and the row badge stay frozen on stale optimizationState until a
  // full page refresh. Subscribe to just the selected test's doc so the button
  // reflects start / cancel / completion immediately, and mirror the state back
  // into the grid row so its badge updates too.
  const [selectedOptimizationStatus, setSelectedOptimizationStatus] =
    useState<OptimizationState["status"] | null>(null);

  useEffect(() => {
    if (singleSelectedTestId == null) {
      setSelectedOptimizationStatus(null);
      return;
    }
    // Seed from the already-loaded grid row to avoid a flicker before the first
    // snapshot lands.
    const seedRow = apiRef?.current?.getRow?.(singleSelectedTestId) as
      | TestTemplate
      | undefined;
    setSelectedOptimizationStatus(seedRow?.optimizationState?.status ?? null);

    const unsubscribe = onSnapshot(
      doc(customFirestore, "custom-tests", singleSelectedTestId),
      (snapshot) => {
        const optimizationState = snapshot.data()?.optimizationState as
          | OptimizationState
          | undefined;
        // Drives the Optimize/Cancel button label. Row badges are kept live by
        // the folder-wide listener in DatatableWrapper, so no row write here.
        setSelectedOptimizationStatus(optimizationState?.status ?? null);
      },
      (error) => {
        trackSentryException(error);
      }
    );
    return () => {
      unsubscribe();
    };
  }, [singleSelectedTestId, apiRef]);

  const optimizeTest = useCallback(() => {
    if (rowSelectionModel.ids.size !== 1) {
      return;
    }
    const [testId] = [...rowSelectionModel.ids] as string[];
    setBuildDialogState((prev) => ({
      ...prev,
      open: true,
      type: "run",
      actionTitle: "Optimize & Validate",
      handleConfirm: async ({
        buildId,
        deviceProviderSettings,
      }: {
        buildId: string;
        deviceProviderSettings?: DeviceProviderSettings;
      }) => {
        try {
          await apiClient.post("/start_test_optimization", {
            organisationId: user.companyId,
            testId,
            uploadId: buildId,
            deviceProviderSettings,
          });
          setCustomAlertState({
            open: true,
            type: "success",
            title: "Optimization started",
            description:
              "Running this test repeatedly until it passes 5 times in a row" +
              " (max 10 runs). Watch progress on the test row.",
          });
        } catch (error) {
          trackSentryException(error);
          const axiosErr = error as AxiosError<{ detail?: string }>;
          const description =
            axiosErr.response?.status === 409
              ? "Optimization already in progress for this test."
              : getErrorMessage("Could not start optimization", error);
          setCustomAlertState({
            open: true,
            type: "error",
            title: "Something went wrong",
            description,
          });
        }
        setRowSelectionModel({ type: "include", ids: new Set() });
      },
    }));
  }, [
    rowSelectionModel,
    setBuildDialogState,
    setCustomAlertState,
    setRowSelectionModel,
    user.companyId,
  ]);

  const setMoveToFolderState = useSetAtom(moveToFolderStateAtom);

  const handleMoveToFolder = useCallback(() => {
    const selectedRows = Array.from(
      apiRef.current.getSelectedRows().values()
    ) as TestTemplate[];
    setMoveToFolderState(selectedRows);
  }, [setMoveToFolderState]);

  const foldersState = useAtomValue(foldersStateAtom);

  const handleFilterUncategorized = useCallback(() => {
    setDisplayUncategorizedOnly((prev) => !prev);
  }, [setDisplayUncategorizedOnly]);

  const companyDataQuery = useAtomValue(companyDataAtom(user.companyId));
  return (
    <div className={"flex gap-2"}>
      {companyDataQuery.data?.settings?.enableSubCompanies && (
        <CloneTestsDialog
          open={cloneTestsDialogOpen}
          setOpen={setCloneTestsDialogOpen}
          testIds={rowSelectionModel}
        ></CloneTestsDialog>
      )}
      {companyDataQuery.data?.settings?.enableSubCompanies && (
        <Button
          disabled={rowSelectionModel.ids.size === 0}
          variant={"outlined"}
          onClick={() => {
            setCloneTestsDialogOpen(true);
          }}
        >
          {`Clone test${rowSelectionModel.ids.size > 1 ? "s" : ""}`}
        </Button>
      )}
      <Button
        variant={"outlined"}
        disabled={
          rowSelectionModel.ids.size === 0 || foldersState.folders?.length === 1
        }
        onClick={handleMoveToFolder}
      >{`Move test${rowSelectionModel.ids.size > 1 ? "s" : ""}`}</Button>
      <Button
        disabled={rowSelectionModel.ids.size === 0}
        variant={"contained"}
        onClick={() => {
          runTests(false);
        }}
      >
        {`Run test${rowSelectionModel.ids.size > 1 ? "s" : ""}`}
      </Button>
      {companyDataQuery.data?.settings?.enableOptimizeAndValidateButton &&
        (selectedOptimizationStatus === "running" ? (
          <Button
            disabled={rowSelectionModel.ids.size !== 1}
            variant={"outlined"}
            color={"warning"}
            onClick={cancelOptimize}
          >
            Cancel optimization
          </Button>
        ) : (
          <Button
            disabled={rowSelectionModel.ids.size !== 1}
            variant={"outlined"}
            onClick={optimizeTest}
          >
            Optimize & Validate
          </Button>
        ))}
      {user.role === "superUser" && (
        <Button
          disabled={rowSelectionModel.ids.size === 0}
          variant={"contained"}
          onClick={() => {
            runTests(true);
          }}
        >
          Run tests x3
        </Button>
      )}
    </div>
  );
};

const OptimizationBadge = ({
  state,
}: {
  state?: OptimizationState;
}) => {
  if (!state) return null;
  const { status } = state;
  const label =
    status === "running"
      ? `Optimizing ${state.consecutivePassCount}/${state.targetConsecutive}`
      : status === "optimized"
        ? "Optimized"
        : status === "blocked_on_app_issue"
          ? "App issue found"
          : status === "failed_to_optimize"
            ? "Optimization failed"
            : status === "cancelled"
              ? "Optimization cancelled"
              : status;
  const cls =
    status === "optimized"
      ? "bg-green-100 text-green-800 border-green-300"
      : status === "running"
        ? "bg-blue-100 text-blue-800 border-blue-300"
        : status === "blocked_on_app_issue"
          ? "bg-red-100 text-red-800 border-red-300"
          : status === "failed_to_optimize"
            ? "bg-orange-100 text-orange-800 border-orange-300"
            : "bg-gray-100 text-gray-700 border-gray-300";
  const tooltipParts: string[] = [
    `runs ${state.totalRunsAttempted}/${state.maxTotalRuns}`,
  ];
  if (state.failureReason) {
    tooltipParts.push(`reason: ${state.failureReason}`);
  }
  return (
    <span
      className={`text-xs px-2 py-0.5 rounded border whitespace-nowrap ${cls}`}
      title={tooltipParts.join(" · ")}
    >
      {label}
    </span>
  );
};

const RunHistoryCell = ({ runHistory }: { runHistory: RunExecution[] }) => {
  const backgroundColorMap = (status: string) => {
    switch (status) {
      case "failed":
        return "bg-red-400";
      case "blocked":
        return "bg-yellow-400";
      case "succeeded":
        return "bg-green-400";
    }
  };

  const timestampToDate = (timestamp: number) => {
    return DateTime.fromJSDate(new Date(timestamp * 1000)).toFormat(
      "dd-MM-yyyy hh:mm a"
    );
  };

  const capitalize = (text: string) => {
    return text.charAt(0).toUpperCase() + text.slice(1);
  };

  const sortedRunHistory = runHistory.sort((a, b) => {
    return a.finishedTimeInEpoch - b.finishedTimeInEpoch;
  });

  return (
    <div className="flex gap-x-1 h-full items-center">
      {sortedRunHistory.map((run, index) => (
        <Tooltip
          title={`${capitalize(run.status)} - ${timestampToDate(
            run.finishedTimeInEpoch
          )}`}
          key={index}
        >
          <a
            href={`/recording/${run.suite_ref}/runs/${run.run_ref}`}
            target="_blank"
            rel="noreferrer"
          >
            <div
              className={`${backgroundColorMap(
                run.status
              )} w-2.5 h-7 rounded-md`}
            />
          </a>
        </Tooltip>
      ))}
    </div>
  );
};

const TagsCell = ({
  params,
}: {
  params: GridRenderCellParams<TestTemplate>;
}) => {
  const queryClient = useQueryClient();
  const tags = useMemo(() => params.value ?? [], [params.value]);
  const { user } = useContext<any>(AuthContext);
  const setCustomAlertState = useSetAtom(customAlertStateAtom);
  const foldersState = useAtomValue(foldersStateAtom);

  const testListQuery = useAtomValue(
    getTestsAtom({
      organizationId: user.companyId,
      folderId: foldersState.selectedFolderId,
    })
  );

  const handleAddTag = useCallback(
    ({ testId, tag }: { testId: string; tag: string }) => {
      if (tags.includes(tag)) {
        setCustomAlertState({
          open: true,
          type: "error",
          title: "Something went wrong",
          description: getErrorMessage("The tag is already set to the test"),
        });
      } else {
        addTagMutation.mutate({ testId, tag });
      }
    },
    [tags]
  );

  const deleteTagMutation = useMutation({
    mutationFn: async ({ testId, tag }: { testId: string; tag: string }) => {
      const testRef = doc(customFirestore, "custom-tests", testId);
      await updateDoc(testRef, {
        tags: arrayRemove(tag),
      });
    },
    onMutate: async ({ tag }: { tag: string }) => {
      // Cancel any outgoing refetches
      // (so they don't overwrite our optimistic update)
      await queryClient.cancelQueries({
        queryKey: ["getTests", user.companyId, foldersState.selectedFolderId],
      });

      // Snapshot the previous value
      const previousTests = queryClient.getQueryData<TestListData>([
        "getTests",
        user.companyId,
        foldersState.selectedFolderId,
      ])!;

      const previousTestsValues = previousTests.values;

      const previousTestIndex = previousTestsValues.findIndex(
        (test) => test.id == params.row.id
      );
      const previousTest = previousTestsValues[previousTestIndex];

      const tagIndexToDelete = previousTest.tags.indexOf(tag);
      const updatedTest = {
        ...previousTest,
        tags: removeItemFromArray(previousTest.tags, tagIndexToDelete),
      };

      queryClient.setQueryData<TestListData>(
        ["getTests", user.companyId, foldersState.selectedFolderId],
        {
          values: replaceItemFomArray(
            previousTestsValues,
            previousTestIndex,
            updatedTest
          ),
          availableTags: previousTests.availableTags,
        }
      );

      return { previousTests };
    },
    onError: (error, _, context) => {
      queryClient.setQueryData(
        ["getTests", user.companyId, foldersState.selectedFolderId],
        context?.previousTests ?? { values: [], availableTags: [] }
      );

      setCustomAlertState({
        open: true,
        type: "error",
        title: "Something went wrong",
        description: getErrorMessage(error),
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: ["getTests", user.companyId, foldersState.selectedFolderId],
      });
    },
  });

  const addTagMutation = useMutation({
    mutationFn: async ({ testId, tag }: { testId: string; tag: string }) => {
      const testRef = doc(customFirestore, "custom-tests", testId);
      await updateDoc(testRef, {
        tags: arrayUnion(tag),
      });
    },
    onMutate: async ({ tag }: { tag: string }) => {
      // Cancel any outgoing refetches
      // (so they don't overwrite our optimistic update)
      await queryClient.cancelQueries({
        queryKey: ["getTests", user.companyId, foldersState.selectedFolderId],
      });

      // Snapshot the previous value
      const previousTests = queryClient.getQueryData<TestListData>([
        "getTests",
        user.companyId,
        foldersState.selectedFolderId,
      ]);

      const previousTestsValues = previousTests!.values;

      const previousTestIndex = previousTestsValues.findIndex(
        (test) => test.id == params.row.id
      );
      const previousTest = previousTestsValues[previousTestIndex];

      const updatedTest = {
        ...previousTest,
        tags: [...(previousTest.tags ?? []), tag],
      };

      const newAvailableTags = previousTests!.availableTags.includes(tag)
        ? previousTests!.availableTags
        : [...previousTests!.availableTags, tag];

      queryClient.setQueryData<TestListData>(
        ["getTests", user.companyId, foldersState.selectedFolderId],
        {
          values: replaceItemFomArray(
            previousTestsValues,
            previousTestIndex,
            updatedTest
          ),
          availableTags: newAvailableTags,
        }
      );

      return { previousTests };
    },
    onError: (error, _, context) => {
      queryClient.setQueryData(
        ["getTests", user.companyId, foldersState.selectedFolderId],
        context?.previousTests ?? []
      );

      setCustomAlertState({
        open: true,
        type: "error",
        title: "Something went wrong",
        description: getErrorMessage(error),
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: ["getTests", user.companyId, foldersState.selectedFolderId],
      });
    },
  });

  const options = useMemo(
    () => (testListQuery.data?.availableTags ?? []).sort(),
    [testListQuery.data]
  );

  return (
    <div className={"overflow-auto w-full h-full flex flex-col justify-center"}>
      {tags && (
        <Autocomplete
          multiple
          id="tags-filled"
          className={"bg-white"}
          options={options}
          value={tags}
          defaultValue={tags}
          freeSolo
          onChange={(e, newValues) => {
            if (newValues.length > tags.length) {
              const tagToAdd = newValues.filter(
                (newValue) => !tags.includes(newValue)
              )[0];
              handleAddTag({
                testId: params.row.id,
                tag: tagToAdd,
              });
            } else {
              const tagToDelete = tags.filter(
                (tag) => !newValues.includes(tag)
              )[0];
              deleteTagMutation.mutate({
                testId: params.row.id,
                tag: tagToDelete,
              });
            }
          }}
          onKeyDown={(event) => {
            if (event.code === "Space") {
              event.stopPropagation();
            }
          }}
          disableClearable={true}
          sx={{
            backgroundColor: "transparent",
            "& .MuiDataGrid-cell": {
              borderColor: "transparent",
            },
            "& .MuiInputBase-root": {
              backgroundColor: "transparent",
              padding: 0,
              ":after": {
                borderBottom: "none",
              },
              ":before": {
                borderBottom: "none",
              },
              ":hover": {
                backgroundColor: "transparent",
                ":after": {
                  borderBottom: "none",
                },
                ":before": {
                  borderBottom: "none",
                },
                ":not(.Mui-disabled, .Mui-error):before": {
                  borderBottom: "none",
                },
              },
            },
            "& .MuiTextField-root": {
              height: "100%",
            },
            "& .MuiAutocomplete-inputRoot": {
              flexWrap: "nowrap",
              marginBlock: "auto",
            },
          }}
          renderTags={(value: readonly string[], getTagProps) =>
            value.map((option: string, index: number) => (
              <Chip
                variant="outlined"
                label={option}
                color={"primary"}
                {...getTagProps({ index })}
                onDelete={() => {
                  deleteTagMutation.mutate({
                    testId: params.row.id,
                    tag: option,
                  });
                }}
              />
            ))
          }
          renderInput={(params) => <TextField {...params} variant="filled" />}
        />
      )}
    </div>
  );
};

function getAsciiSum(str) {
  let sum = 0;
  for (let i = 0; i < str.length; i++) {
    sum += str.charCodeAt(i);
  }
  return sum;
}

const tagsComparator: GridComparatorFn<string[]> = (
  listA: string[] | null | undefined,
  listB: string[] | null | undefined
): number => {
  // If listA or listB is null or undefined, handle those cases first
  if (listA == null && listB != null) return -1;
  if (listB == null && listA != null) return 1;
  if (listA == null && listB == null) return 0;

  if (listA!.length === listB!.length) {
    return (
      listA!.reduce((prev, current) => prev + getAsciiSum(current), 0) -
      listB!.reduce((prev, current) => prev + getAsciiSum(current), 0)
    );
  }
  return listA!.length - listB!.length;
};

const runHistoryComparator: GridComparatorFn<RunExecution[]> = (
  listA: RunExecution[] | null | undefined,
  listB: RunExecution[] | null | undefined
): number => {
  const statusScore = (status: string) => {
    switch (status) {
      case "succeeded":
        return 1;
      case "blocked":
        return 0;
      case "failed":
        return -1;
      default:
        return 0;
    }
  };

  // If listA or listB is empty, handle those cases first
  if (listA!.length == 0 && listB!.length > 0) return -1;
  if (listB!.length == 0 && listA!.length > 0) return 1;
  if (listA!.length == 0 && listB!.length == 0) return 0;

  // Compare based on the score of the inner lists. Scores for each test runs are:
  // +1 for succeeded test, 0 for blocked test and -1 for failed test
  const listAScores: number[] = listA!.map((runExecution) =>
    statusScore(runExecution.status)
  );
  const listBScores: number[] = listB!.map((runExecution) =>
    statusScore(runExecution.status)
  );

  const scoreA = listAScores.reduce((partialSum, a) => partialSum + a, 0);
  const scoreB = listBScores.reduce((partialSum, a) => partialSum + a, 0);

  return scoreA - scoreB;
};

const expandFoldersAtom = atomWithStorage("expandFolders", true);

export default TestsHome;
